import { useFolderById } from "@/hooks/use-workspace-data";
/**
 * Breadcrumbs for the workspace.
 *
 * Renders the chain of folders from the workspace root to the current
 * selection. Clicking a crumb sets the current folder; clicking "Workspace"
 * goes back to the root.
 */
import { ChevronRight, Home } from "lucide-react";

export interface WorkspaceBreadcrumbsProps {
  /** Ordered list of folder ids, root → leaf. */
  path: string[];
  onSelect: (folderId: string | null) => void;
}

export function WorkspaceBreadcrumbs({ path, onSelect }: WorkspaceBreadcrumbsProps): JSX.Element {
  return (
    <nav aria-label="Workspace path" className="flex items-center gap-1 text-sm">
      <button
        type="button"
        onClick={() => onSelect(null)}
        className="flex items-center gap-1 rounded-md px-2 py-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
      >
        <Home className="h-4 w-4" />
        <span>Workspace</span>
      </button>
      {path.map((id, idx) => (
        <Crumb key={id} id={id} isLast={idx === path.length - 1} onSelect={onSelect} />
      ))}
    </nav>
  );
}

interface CrumbProps {
  id: string;
  isLast: boolean;
  onSelect: (folderId: string | null) => void;
}

function Crumb({ id, isLast, onSelect }: CrumbProps): JSX.Element {
  const folder = useFolderById(id);
  return (
    <>
      <ChevronRight className="h-4 w-4 text-muted-foreground/60" aria-hidden />
      <button
        type="button"
        disabled={isLast}
        onClick={() => onSelect(id)}
        className={
          isLast
            ? "cursor-default rounded-md px-2 py-1 font-medium text-foreground"
            : "rounded-md px-2 py-1 text-muted-foreground hover:bg-accent hover:text-accent-foreground"
        }
      >
        {folder?.name ?? "…"}
      </button>
    </>
  );
}
